import {
    Account,
    AccountGroupHierarchyNode,
    AccountRole,
} from "@common/types/domains/accounting/account";
import {
    accountSortComparator,
    buildAccountIdentityKey,
} from "@common/helpers/accounting";

export type AccountGroupingIdentity = "nameTypeSubtype" | "nameTypePath";

export interface MultiEntityAccountConstituent {
    id: string;
    entityId: number;
    codeText: string | null;
    parentGroupId: string | null;
    sortOrder: number;
}

export type MultiEntityAccount = Account & {
    key: string;
    entityIds: number[];
    codeTexts: string[];
    constituents: MultiEntityAccountConstituent[];
};

export interface GroupAccountsByIdentityParams {
    accounts?: Array<Account & { entityId: number }>;
    selectedEntityIds?: number[];
    coverage?: "any" | "all";
    identity?: AccountGroupingIdentity;
    groupsByEntityId?: Map<number, AccountGroupHierarchyNode[]>;
    showAccountNumbers: boolean;
}

const nameTypeSubtypeKey = (account: Account) =>
    `${account.name.trim().toLowerCase()}-${account.type}-${account.subtype}`;

const nameTypePathKey = (
    account: Account & { entityId: number },
    groupsByEntityId: Map<number, AccountGroupHierarchyNode[]> | undefined,
) =>
    buildAccountIdentityKey({
        name: account.name,
        type: account.type,
        parentGroupId: account.parentGroupId,
        groups: groupsByEntityId?.get(Number(account.entityId)) ?? [],
    });

const hasOneAccountPerEntity = (row: MultiEntityAccount) =>
    new Set(row.constituents.map((constituent) => constituent.entityId))
        .size === row.constituents.length;

export function groupAccountsByIdentity({
    accounts,
    selectedEntityIds,
    coverage = "any",
    identity = "nameTypeSubtype",
    groupsByEntityId,
    showAccountNumbers,
}: GroupAccountsByIdentityParams): MultiEntityAccount[] {
    if (!accounts || (identity === "nameTypePath" && !groupsByEntityId)) {
        return [];
    }

    const rowsByKey = new Map<string, MultiEntityAccount>();

    const sortedAccounts = accounts.toSorted((a, b) =>
        accountSortComparator(a, b, showAccountNumbers),
    );

    for (const account of sortedAccounts) {
        if (
            account.financialAccountId ||
            account.role === AccountRole.cashOnHand
        ) {
            continue;
        }

        if (account.isDisabled) {
            continue;
        }

        if (!selectedEntityIds?.includes(Number(account.entityId))) {
            continue;
        }

        const key =
            identity === "nameTypePath"
                ? nameTypePathKey(account, groupsByEntityId)
                : nameTypeSubtypeKey(account);
        const constituent = {
            id: account.id,
            entityId: Number(account.entityId),
            codeText: account.codeText,
            parentGroupId: account.parentGroupId,
            sortOrder: account.sortOrder,
        };
        const existing = rowsByKey.get(key);

        if (existing) {
            existing.entityIds.push(Number(account.entityId));
            existing.constituents.push(constituent);
            if (
                account.codeText &&
                !existing.codeTexts.includes(account.codeText)
            ) {
                existing.codeTexts.push(account.codeText);
            }
        } else {
            rowsByKey.set(key, {
                ...account,
                key,
                entityIds: [Number(account.entityId)],
                codeTexts: account.codeText ? [account.codeText] : [],
                constituents: [constituent],
            });
        }
    }

    const dedupedAccounts = [...rowsByKey.values()];

    const coveredAccounts =
        coverage === "all" && selectedEntityIds?.length
            ? dedupedAccounts.filter((account) =>
                  selectedEntityIds.every((id) =>
                      account.entityIds.includes(id),
                  ),
              )
            : dedupedAccounts;

    return identity === "nameTypePath"
        ? coveredAccounts.filter(hasOneAccountPerEntity)
        : coveredAccounts;
}
